/* Channels tab — signal-routing surface (extracted from Dashboard.jsx).
   Includes ExchangeSummaryCard (also used by Overview tab), ChannelsSection,
   and the per-row ChannelRow (kill switch, edit dialog trigger, delete
   confirm). */
import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { channels, settings, observability, analytics } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge, cn } from "@/components/atoms";
import ChannelEditDialog from "@/pages/channels/ChannelEditDialog";
import ChannelDetailSection from "@/pages/channels/ChannelDetailSection";
import PnLLeaderboard from "@/pages/analytics/PnLLeaderboard";
import { Trash, Copy, ListBullets } from "@phosphor-icons/react";

const ROW_GRADIENTS = [
  "linear-gradient(135deg,#4338CA,#6D5FF0)",
  "linear-gradient(135deg,#0F766E,#14B8A6)",
  "linear-gradient(135deg,#C2410C,#F59E0B)",
  "linear-gradient(135deg,#6D28D9,#A855F7)",
  "linear-gradient(135deg,#BE185D,#EC4899)",
  "linear-gradient(135deg,#1D4ED8,#38BDF8)",
];

/* Reusable summary tile — colour + label switch based on
   (auto_trading × paper_mode × connected). Also used by Overview. */
export function ExchangeSummaryCard({ exchange, summary: s }) {
  const paper = s.paper_mode;
  const connected = s.status === "connected" || s.status === "ok";
  const autoOn = !!s.auto_trading;
  let stateLabel, stateTone, stateHint;
  if (!autoOn) {
    stateLabel = "AUTO OFF";
    stateTone = "loss";
    stateHint = "Signals parsed but not executed";
  } else if (paper) {
    stateLabel = "PAPER · ACTIVE";
    stateTone = "warn";
    stateHint = "Auto-executing simulated fills";
  } else if (connected) {
    stateLabel = "LIVE · ACTIVE";
    stateTone = "profit";
    stateHint = "Auto-executing real orders";
  } else {
    stateLabel = (s.status || "not configured").toUpperCase();
    stateTone = "loss";
    stateHint = "Check API keys in Setup";
  }
  return (
    <div className={cn(
           "rounded-2xl border p-5 transition-colors card-surface",
           autoOn && !paper && connected
             ? "bg-[#E7F7F1] border-emerald-200"
             : autoOn && paper
             ? "bg-[#FEF3E2] border-amber-200"
             : !autoOn
             ? "bg-slate-50 border-slate-200"
             : "bg-[#FDECEC] border-red-200",
         )}
         data-testid={`ex-summary-${exchange}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <p className="font-display text-lg uppercase tracking-tight">{exchange}</p>
          <Badge variant={stateTone} className="text-[10px]">{stateLabel}</Badge>
        </div>
        <span className="text-[11px] text-slate-500 shrink-0">
          {s.channels_count} channel{s.channels_count === 1 ? "" : "s"}
        </span>
      </div>
      <p className="text-[11px] text-slate-500 mb-3">{stateHint}</p>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Balance</p>
          <p className="mono font-semibold">
            {paper ? "Paper" :
              s.balance_usdt != null
                ? `$${Number(s.balance_usdt).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                : "—"}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Open pos.</p>
          <p className="mono font-semibold">{s.open_positions}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500">uPnL</p>
          <p className={cn("mono font-semibold",
            s.unrealized_pnl > 0 && "text-emerald-600",
            s.unrealized_pnl < 0 && "text-red-600")}>
            ${Number(s.unrealized_pnl).toFixed(2)}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500">24h Realized</p>
          <p className={cn("mono font-semibold",
            s.realized_pnl_24h > 0 && "text-emerald-600",
            s.realized_pnl_24h < 0 && "text-red-600")}>
            ${Number(s.realized_pnl_24h).toFixed(2)}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function ChannelsSection({ viewingChannelId = null, onSelectChannel, onBack } = {}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["channels"],
    queryFn: async () => (await channels.list()).data,
    refetchInterval: 6000,
  });
  const tgQ = useQuery({ queryKey: ["tg"], queryFn: async () => (await settings.getTelegram()).data });
  const ovQ = useQuery({ queryKey: ["overview"], queryFn: async () => (await observability.overview()).data, refetchInterval: 4000 });
  const pnlQ = useQuery({ queryKey: ["per-channel-pnl"], queryFn: async () => (await analytics.perChannelPnl()).data, refetchInterval: 15_000 });
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");

  const list = q.data || [];
  const visibleList = search.trim()
    ? list.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase()) || c.exchange.toLowerCase().includes(search.trim().toLowerCase()))
    : list;
  const pnlByChannel = React.useMemo(() => {
    const m = {};
    for (const r of pnlQ.data || []) m[r.channel_id] = r;
    return m;
  }, [pnlQ.data]);

  const liveCount = list.filter((c) => !ovQ.data?.by_exchange?.[c.exchange]?.paper_mode).length;
  const paperCount = list.length - liveCount;
  const openTotal = Object.values(pnlByChannel).reduce((s, r) => s + (r.open_count || 0), 0);

  if (viewingChannelId) {
    const idx = list.findIndex((c) => c.id === viewingChannelId);
    return (
      <ChannelDetailSection
        channelId={viewingChannelId}
        colorIndex={idx >= 0 ? idx : 0}
        onBack={onBack}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="section-channels">
      {list.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card-surface p-5">
            <p className="overline">Channels configured</p>
            <p className="mono text-2xl font-bold mt-2">{list.length}</p>
          </div>
          <div className="card-surface p-5">
            <p className="overline">Live channels</p>
            <p className="mono text-2xl font-bold mt-2 text-emerald-600">{liveCount}</p>
          </div>
          <div className="card-surface p-5">
            <p className="overline">Paper channels</p>
            <p className="mono text-2xl font-bold mt-2 text-amber-600">{paperCount}</p>
          </div>
          <div className="card-surface p-5">
            <p className="overline">Open positions</p>
            <p className="mono text-2xl font-bold mt-2">{openTotal}</p>
          </div>
        </div>
      )}

      <PnLLeaderboard />
      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="overline">Signal routing</p>
            <h3 className="font-display text-xl mt-1">Channels</h3>
            <p className="text-sm text-slate-500 mt-1">
              Route Telegram sources to specific exchanges with independent risk profiles and kill switches.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {list.length > 0 && (
              <>
                <input
                  data-testid="channels-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search channels..."
                  className="h-9 w-48 px-3 rounded-md border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/30"
                />
                <Button
                  variant="outline" size="sm" className="border-slate-300"
                  data-testid="channels-export"
                  onClick={() => {
                    const rows = [["name", "exchange", "enabled", "risk_percent", "max_leverage", "realized_pnl", "win_rate_pct", "trades_count"],
                      ...visibleList.map((c) => {
                        const p = pnlByChannel[c.id];
                        return [c.name, c.exchange, c.enabled, c.risk_profile?.risk_percent_per_trade ?? "", c.risk_profile?.max_leverage ?? "",
                          p?.realized_pnl ?? "", p?.win_rate_pct ?? "", p?.trades_count ?? ""];
                      })];
                    const csv = rows.map((r) => r.map((v) => `"${v ?? ""}"`).join(",")).join("\n");
                    const blob = new Blob([csv], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url; a.download = "channels.csv"; a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  <ListBullets size={16} className="mr-2" /> Export CSV
                </Button>
              </>
            )}
            <Button
              data-testid="new-channel-btn"
              onClick={() => setEditing("new")}
              className="bg-[#4F46E5] hover:bg-[#4338CA]"
            >
              + New channel
            </Button>
          </div>
        </div>

        {list.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-300 rounded-md">
            No channels yet. Click <strong>New channel</strong> to create your first routing rule.
          </div>
        ) : visibleList.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-300 rounded-md">
            No channels match "{search}".
          </div>
        ) : (
          <div className="space-y-2">
            {visibleList.map((c, i) => (
              <ChannelRow
                key={c.id}
                ch={c}
                index={i}
                pnl={pnlByChannel[c.id]}
                paperMode={!!ovQ.data?.by_exchange?.[c.exchange]?.paper_mode}
                onView={() => onSelectChannel?.(c.id)}
                onEdit={() => setEditing(c)}
                onKill={(active, reason) => channels.killSwitch(c.id, active, reason).then(() => qc.invalidateQueries({ queryKey: ["channels"] }))}
                onToggleEnabled={(next) => channels.update(c.id, { enabled: next }).then(() => qc.invalidateQueries({ queryKey: ["channels"] }))}
                onDuplicate={() => channels.duplicate(c.id, `${c.name} (copy)`).then(() => qc.invalidateQueries({ queryKey: ["channels"] }))}
                onDelete={() => channels.remove(c.id)
                  .then(() => { toast.success(`Deleted "${c.name}"`); qc.invalidateQueries({ queryKey: ["channels"] }); })
                  .catch((e) => toast.error("Delete failed: " + (e?.response?.data?.detail || e.message)))}
              />
            ))}
          </div>
        )}
      </div>

      {editing && (
        <ChannelEditDialog
          channel={editing === "new" ? null : editing}
          telegramSourceId={tgQ.data?.id}
          onClose={() => setEditing(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ["channels"] }); setEditing(null); }}
        />
      )}
    </div>
  );
}

function ChannelRow({ ch, index, pnl, paperMode, onView, onEdit, onKill, onToggleEnabled, onDuplicate, onDelete }) {
  const kill = ch.kill_switch_active;
  const winRate = pnl && (pnl.wins + pnl.losses) > 0 ? `${pnl.win_rate_pct}%` : "—";
  return (
    <div
      className={cn(
        "rounded-xl border p-3",
        kill ? "border-red-300 bg-red-50" :
        ch.enabled ? "border-slate-200 bg-white hover:bg-slate-50" :
                     "border-slate-200 bg-slate-50 opacity-70",
      )}
      data-testid={`channel-row-${ch.id}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-lg shrink-0"
            style={{ background: ROW_GRADIENTS[index % ROW_GRADIENTS.length] }}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <button
                onClick={onView}
                className="font-semibold text-slate-900 hover:text-[#4F46E5] hover:underline transition-colors"
                data-testid={`channel-view-${ch.id}`}
              >
                {ch.name}
              </button>
              <Badge variant={ch.exchange === "weex" ? "profit" : ch.exchange === "bitget" ? "warn" : "info"} className="uppercase">
                {ch.exchange}
              </Badge>
              <Badge variant={paperMode ? "warn" : "profit"}>{paperMode ? "● Paper" : "● Live"}</Badge>
              {kill && <Badge variant="loss">KILLED</Badge>}
              {!ch.enabled && !kill && <Badge variant="muted">PAUSED</Badge>}
            </div>
            <p className="text-xs text-slate-500 mono">
              risk {ch.risk_profile?.risk_percent_per_trade ?? "—"}% · max lev {ch.risk_profile?.max_leverage ?? "—"}x
              {ch.risk_profile?.max_notional_per_trade_usdt ? ` · $${ch.risk_profile.max_notional_per_trade_usdt}/trade cap` : ""}
            </p>
            {ch.telegram_chat_label && (
              <p className="text-[11px] text-slate-500 mt-0.5" title={ch.telegram_chat_id}>
                📡 <span className="text-slate-700">{ch.telegram_chat_label}</span>
              </p>
            )}
            {kill && ch.kill_switch_reason && (
              <p className="text-xs text-red-700 mt-1">Killed: {ch.kill_switch_reason}</p>
            )}
          </div>
        </div>

        {pnl && (
          <div className="flex items-center gap-5 mono text-sm shrink-0">
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-widest text-slate-500">Realized</p>
              <p className={cn("font-bold", pnl.realized_pnl > 0 ? "text-emerald-600" : pnl.realized_pnl < 0 ? "text-red-600" : "")}>
                {pnl.realized_pnl >= 0 ? "+" : "-"}${Math.abs(pnl.realized_pnl).toFixed(2)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-widest text-slate-500">Win rate</p>
              <p className="font-bold">{winRate}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-widest text-slate-500">Trades</p>
              <p className="font-bold">{pnl.trades_count}{pnl.open_count > 0 ? ` (${pnl.open_count} open)` : ""}</p>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 shrink-0">
          <Switch
            checked={ch.enabled}
            onCheckedChange={onToggleEnabled}
            data-testid={`channel-toggle-${ch.id}`}
            disabled={kill}
          />
          <Button
            size="sm"
            variant={kill ? "outline" : "ghost"}
            className={kill ? "border-red-400 text-red-700 hover:bg-red-100" : "text-red-600 hover:bg-red-50"}
            onClick={() => onKill(!kill, kill ? "" : "manual")}
            data-testid={`channel-kill-${ch.id}`}
          >
            {kill ? "Clear kill" : "Kill"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`channel-edit-${ch.id}`}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={onDuplicate} title="Duplicate" data-testid={`channel-dup-${ch.id}`}>
            <Copy size={16} />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="text-red-600 hover:bg-red-50"
                data-testid={`channel-delete-${ch.id}`}
                title="Delete channel"
              >
                <Trash size={16} />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid={`channel-delete-dialog-${ch.id}`}>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete channel &ldquo;{ch.name}&rdquo;?</AlertDialogTitle>
                <AlertDialogDescription>
                  The channel will stop routing signals. Trade history is retained for auditing.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid={`channel-delete-cancel-${ch.id}`}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid={`channel-delete-confirm-${ch.id}`}
                  onClick={onDelete}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}
